import { useEffect, useRef, useState } from 'react';
import {
  areNotificationsSupported,
  getNotificationPermission,
  requestNotificationPermission,
  showMessageNotification,
} from '../utils/notifications';
import { useChatStore } from '../stores/chatStore';

/**
 * Hook for managing browser notifications
 */
export function useNotifications(enabled: boolean = true) {
  const [permission, setPermission] = useState<NotificationPermission>(getNotificationPermission());
  const messages = useChatStore((state) => state.messages);
  const lastNotifiedIdRef = useRef<string | null>(null);

  // Request permission on mount if not already granted or denied
  useEffect(() => {
    if (enabled && permission === 'default' && areNotificationsSupported()) {
      requestNotificationPermission().then(setPermission);
    }
  }, [enabled, permission]);

  // Show notification for new bot messages
  useEffect(() => {
    if (!enabled || messages.length === 0 || permission !== 'granted') {
      return;
    }

    // Messages are stored newest-first
    const newestMessage = messages[0];

    // Only show notification for bot messages we haven't already notified about
    if (newestMessage.sender === 'bot' && newestMessage.id !== lastNotifiedIdRef.current) {
      lastNotifiedIdRef.current = newestMessage.id;
      showMessageNotification(newestMessage.content, 'Support Bot');
    }
  }, [enabled, messages, permission]);

  const requestPermission = async () => {
    const newPermission = await requestNotificationPermission();
    setPermission(newPermission);
    return newPermission;
  };

  return {
    permission,
    supported: areNotificationsSupported(),
    requestPermission,
  };
}
